import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit {
  eventForm: FormGroup;
  showEventModal = false;
  showSubEventsSection = false;
  showViewModal = false;
  isLoadingViewModal = false;
  events: any[] = [];
  selectedEvent: any = null;
  destroy$ = new Subject<void>();
  bannerImages: { [key: number]: SafeUrl } = {};
  logoImages: { [key: number]: SafeUrl } = {};
  posterImages: { [key: number]: SafeUrl } = {};

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    this.eventForm = this.fb.group({
      mainEventName: [''],
      mainEventLink: [''],
      subEvents: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadEvents();
  }

  // Getter for subEvents FormArray
  get subEventsArray(): FormArray {
    return this.eventForm.get('subEvents') as FormArray;
  }

  // Create a sub event form group
  createSubEventForm(): FormGroup {
    return this.fb.group({
      subEventName: [''],
      subEventLink: [''],
      isWebsiteReady: [true],
      startDate: [''],
      endDate: [''],
      startTime: [''],
      endTime: [''],
      logoPath: [''],
      fileData: [''],
      fileType: [''],
      posterImage: [''],
      posterFileData: [''],
      posterFileType: [''],
      websiteName: window.location.hostname
    });
  }

  // Add a new sub event
  addSubEvent(): void {
    this.subEventsArray.push(this.createSubEventForm());
  }

  // Toggle sub events visibility and add first sub event
  toggleSubEventsVisibility(): void {
    if (!this.showSubEventsSection) {
      this.showSubEventsSection = true;
      // Add first sub event if none exist
      if (this.subEventsArray.length === 0) {
        this.subEventsArray.push(this.createSubEventForm());
      }
    } else {
      this.showSubEventsSection = false;
    }
  }

  // Remove a sub event
  removeSubEvent(index: number): void {
    this.subEventsArray.removeAt(index);
  }

  openEventModal(): void {
    this.eventForm.reset();
    this.subEventsArray.clear();
    this.showSubEventsSection = false;
    if (this.eventForm.get('mainEventSeq')) {
      this.eventForm.removeControl('mainEventSeq');
    }
    this.showEventModal = true;
  }

  closeEventModal(): void {
    this.showEventModal = false;
    this.showSubEventsSection = false;
    this.eventForm.reset();
    this.subEventsArray.clear();
    if (this.eventForm.get('mainEventSeq')) {
      this.eventForm.removeControl('mainEventSeq');
    }
  }

  onSubmit(): void {
    if (this.eventForm.valid) {
      if (this.eventForm.get('mainEventSeq')) {
        this.updateEventDetails();
      } else {
        this.saveEventdetails();
      }
    }
  }

  viewEvent(eventSeq: number): void {
    const event = this.events.find(e => e.mainEventSeq === eventSeq);
    if (event) {
      console.log('Viewing event:', event);
      this.selectedEvent = event;
      this.isLoadingViewModal = true;
      this.spinner.show();
      // Fetch sub-events using the API endpoint
      this.appService.getAllSubEventsByMainEventSeq(eventSeq)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (subEvents) => {
            console.log('Sub-events loaded successfully:', subEvents);
            this.selectedEvent.subEvents = subEvents;
            // Load logo images for each sub event
            subEvents.forEach((sub: any) => {
              if (sub.subEventId) {
                if (sub.isWebsiteReady !== false) {
                  this.loadSubEventLogo(sub.subEventId);
                } else {
                  this.loadSubEventPoster(sub.subEventId);
                }
              }
            });
            this.showViewModal = true;
            this.isLoadingViewModal = false;
            this.spinner.hide();
          },
          error: (err) => {
            console.error('Error loading sub-events:', err);
            // Show modal with event data even if sub-events fail
            this.selectedEvent.subEvents = event.subEvents || [];
            this.showViewModal = true;
            this.isLoadingViewModal = false;
            this.spinner.hide();
          }
        });
    } else {
      this.toastr.error('Event not found');
    }
  }

  // getBannerImageUrl(subEventId: number): SafeUrl | null {
  //   return this.bannerImages[subEventId] || null;
  // }


  deleteMainEvent(mainEventSeq: number): void {
    if (confirm('Are you sure you want to delete this main event?')) {
      this.spinner.show();
      this.appService.deleteMainEventById(mainEventSeq)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastr.success('Main event deleted successfully!');
            this.closeViewModal();
            this.loadEvents();
          },
          error: (err) => {
            this.toastr.error(err.message || 'Failed to delete main event');
          },
          complete: () => {
            setTimeout(() => this.spinner.hide(), 1000);
          }
        });
    }
  }

  deleteSubEvent(subEventId: number, index: number) {
    if (this.showViewModal) {
      // Always call API in view modal
      if (confirm('Are you sure you want to delete this sub event?')) {
        this.spinner.show();
        this.appService.deleteSubEventById(subEventId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastr.success('Sub event deleted successfully!');
              if (this.selectedEvent && this.selectedEvent.subEvents) {
                this.selectedEvent.subEvents.splice(index, 1);
              }
            },
            error: (err) => {
              this.toastr.error(err.message || 'Failed to delete sub event');
            },
            complete: () => {
              setTimeout(() => this.spinner.hide(), 1000);
            }
          });
      }
    } else {
      // Form/Edit mode: call API if subEventId exists, then remove from form array
      const subEventGroup = this.subEventsArray.at(index);
      const id = subEventGroup?.get('subEventId')?.value;
      if (id) {
        if (confirm('Are you sure you want to delete this sub event?')) {
          this.spinner.show();
          this.appService.deleteSubEventById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Sub event deleted successfully!');
                this.removeSubEvent(index);
              },
              error: (err) => {
                this.toastr.error(err.message || 'Failed to delete sub event');
              },
              complete: () => {
                setTimeout(() => this.spinner.hide(), 1000);
              }
            });
        }
      } else {
        // New sub event not yet saved — just remove locally
        this.removeSubEvent(index);
      }
    }
  }

  loadSubEventLogo(subEventId: number): void {
    this.appService.getSubEventLogoById(subEventId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const url = window.URL.createObjectURL(blob);
            this.logoImages[subEventId] = this.sanitizer.bypassSecurityTrustUrl(url);
            this.cdRef.detectChanges();
          }
        },
        error: (err) => {
          console.error(`Error loading logo for sub event ${subEventId}:`, err);
        }
      });
  }

  loadSubEventPoster(subEventId: number): void {
    this.appService.getSubEventPosterImageById(subEventId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const url = window.URL.createObjectURL(blob);
            this.posterImages[subEventId] = this.sanitizer.bypassSecurityTrustUrl(url);
            this.cdRef.detectChanges();
          }
        },
        error: (err) => {
          console.error(`Error loading poster for sub event ${subEventId}:`, err);
        }
      });
  }

  closeViewModal() {
    this.showViewModal = false;
    this.selectedEvent = null;
  }

  getSubEventNames(subEvents: any[] | undefined): string {
    if (!subEvents || subEvents.length === 0) {
      return 'N/A';
    }
    return subEvents.map(event => event.subEventName).join(', ');
  }

  onSubEventFileChange(event: any, index: number) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const subEventGroup = this.subEventsArray.at(index) as FormGroup;
        subEventGroup.patchValue({
          fileData: base64,
          fileType: file.type,
          logoPath: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  }

  onPosterFileChange(event: any, index: number) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const subEventGroup = this.subEventsArray.at(index) as FormGroup;
        subEventGroup.patchValue({
          posterFileData: base64,
          posterFileType: file.type,
          posterImage: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  }

  updateEventDetails() {
    this.spinner.show();
    // Only send the fields that match the SubEvent interface
    const filteredSubEvents = this.subEventsArray.value.map((sub: any) => {
      return {
        subEventId: sub.subEventId,
        subEventName: sub.subEventName,
        subEventLink: sub.subEventLink,
        isWebsiteReady: sub.isWebsiteReady,
        startDate: sub.startDate,
        endDate: sub.endDate,
        startTime: sub.startTime,
        endTime: sub.endTime,
        logoPath: sub.logoPath,
        fileData: sub.fileData,
        fileType: sub.fileType,
        posterImage: sub.posterImage,
        posterFileData: sub.posterFileData,
        posterFileType: sub.posterFileType,
        websiteName: sub.websiteName
      };
    });
    const payload = {
      mainEventSeq: this.eventForm.get('mainEventSeq')?.value,
      mainEventName: this.eventForm.get('mainEventName')?.value,
      mainEventLink: this.eventForm.get('mainEventLink')?.value,
      subEvent: filteredSubEvents
    };
    console.log('Update Event Payload:', payload);
    this.appService.updateMainEvent(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Event updated successfully!');
          this.eventForm.reset();
          this.closeEventModal();
          this.loadEvents();
        },
        error: (error) => {
          this.toastr.error(error.message || 'Failed to update event');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        }
      });
  }

  editEvent(eventSeq: number): void {
    const event = this.events.find(e => e.mainEventSeq === eventSeq);
    if (event) {
      this.eventForm.reset();
      this.showEventModal = true;
      this.showSubEventsSection = true;
      this.eventForm.patchValue({
        mainEventName: event.mainEventName,
        mainEventLink: event.mainEventLink
      });
      // Clear and repopulate subEvents
      this.subEventsArray.clear();
      if (event.subEvents && event.subEvents.length > 0) {
        event.subEvents.forEach((sub: any) => {
          this.subEventsArray.push(this.fb.group({
            subEventId: [sub.subEventId || ''],
            subEventName: [sub.subEventName || ''],
            subEventLink: [sub.subEventLink || ''],
            isWebsiteReady: [sub.isWebsiteReady !== undefined ? sub.isWebsiteReady : true],
            startDate: [sub.startDate || ''],
            endDate: [sub.endDate || ''],
            startTime: [sub.startTime || ''],
            endTime: [sub.endTime || ''],
            logoPath: [sub.logoPath || ''],
            fileData: [sub.fileData || ''],
            fileType: [sub.fileType || ''],
            posterImage: [sub.posterImage || ''],
            posterFileData: [sub.posterFileData || ''],
            posterFileType: [sub.posterFileType || ''],
            websiteName: [sub.websiteName || '']
          }));
        });
      }
      // Optionally store the eventSeq for update logic
      if (!this.eventForm.get('mainEventSeq')) {
        this.eventForm.addControl('mainEventSeq', this.fb.control(event.mainEventSeq));
      } else {
        this.eventForm.get('mainEventSeq')?.setValue(event.mainEventSeq);
      }
    } else {
      this.toastr.error('Event not found');
    }
  }



  saveEventdetails(): void {
    this.spinner.show();

    const payload = {
      mainEventName: this.eventForm.get('mainEventName')?.value,
      mainEventLink: this.eventForm.get('mainEventLink')?.value,
      subEvent: this.subEventsArray.value
    };

    console.log('Saving payload:', payload);
    console.log('Sub Events Count:', this.subEventsArray.length);
    console.log('Sub Events Data:', this.subEventsArray.value);

    this.appService.saveEventsDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Event details saved successfully!');
          this.eventForm.reset();
          this.closeEventModal();
          this.loadEvents();
        },
        error: (error) => {
          this.toastr.error(error.message || 'Something went wrong, please try again');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        }
      });
  }

  loadEvents(): void {
    this.spinner.show();
    this.appService.getAllEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.events = data.sort((a: any, b: any) => b.mainEventSeq - a.mainEventSeq);
          // Fetch sub events for each event
          this.events.forEach(event => {
            this.spinner.show();
            this.appService.getAllSubEventsByMainEventSeq(event.mainEventSeq)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (subEvents) => {
                  event.subEvents = subEvents;
                  this.spinner.hide();
                },
                error: (err) => {
                  console.error('Error loading sub-events for event:', event.mainEventSeq, err);
                  event.subEvents = [];
                  this.spinner.hide();
                }
              });
          });
        },
        error: (err) => {
          console.error('Error loading events:', err);
          this.toastr.error('Failed to load events');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        },
      });
  }

}

